import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, ShieldCheck, UserPlus, Users } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import BrandLogo from 'src/shared/components/BrandLogo';
import { ActionButton, ContainerBox, FlexContainer, TypographyText } from 'src/tef/components';

interface WelcomeExperienceScreenProps {
  planType?: string;
  onComplete: () => void;
}

interface Panel {
  id: string;
  icon: React.ElementType;
  iconAccent: string;
  iconRing: string;
  title: string;
  body: string;
  ctaText: string;
}

const PANELS: Panel[] = [
  {
    id: 'client-centric',
    icon: Users,
    iconAccent: 'text-blue-600 dark:text-blue-300',
    iconRing: 'bg-blue-50 border-blue-100 dark:bg-blue-400/10 dark:border-blue-400/20',
    title: 'Tropel works around your clients',
    body: 'Every research, document, and memo lives under a client.',
    ctaText: 'Continue',
  },
  {
    id: 'data-safety',
    icon: ShieldCheck,
    iconAccent: 'text-emerald-600 dark:text-emerald-300',
    iconRing: 'bg-emerald-50 border-emerald-100 dark:bg-emerald-400/10 dark:border-emerald-400/20',
    title: 'Your client data is protected',
    body: 'Encrypted at every step. Only you see your data, and it never trains external models.',
    ctaText: 'Continue',
  },
  {
    id: 'first-client',
    icon: UserPlus,
    iconAccent: 'text-amber-600 dark:text-amber-300',
    iconRing: 'bg-amber-50 border-amber-100 dark:bg-amber-400/10 dark:border-amber-400/20',
    title: "Let's add your first client",
    body: 'Add a client to start research, uploads, and memos.',
    ctaText: 'Get started',
  },
];

const WelcomeExperienceScreen: React.FC<WelcomeExperienceScreenProps> = ({ onComplete }) => {
  const [index, setIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const panel = PANELS[index];
  const PanelIcon = panel.icon;
  const isLast = index === PANELS.length - 1;

  const handleNext = useCallback(() => {
    if (isLast) {
      setFadeOut(true);
      setTimeout(onComplete, 400);
      return;
    }
    setIndex((i) => i + 1);
  }, [isLast, onComplete]);

  return (
    <ContainerBox
      className={`fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-950 overflow-hidden transition-opacity duration-500 ${fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <ContainerBox className="relative z-10 flex flex-col flex-1 w-full max-w-xl mx-auto px-6 sm:px-8 py-8 sm:py-12 overflow-y-auto">
        <ContainerBox className="flex justify-center mb-10 sm:mb-14 shrink-0">
          <ContainerBox className="dark:hidden">
            <BrandLogo size="md" />
          </ContainerBox>
          <ContainerBox className="hidden dark:block">
            <BrandLogo variant="white" size="md" />
          </ContainerBox>
        </ContainerBox>

        <ContainerBox className="flex-1 flex flex-col justify-center min-h-0">
          <motion.div
            key={panel.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-center"
          >
            <FlexContainer justify="center" className="mb-8 sm:mb-10">
              <ContainerBox
                className={`w-20 h-20 sm:w-24 sm:h-24 rounded-3xl border flex items-center justify-center ${panel.iconRing}`}
              >
                <PanelIcon className={`w-10 h-10 sm:w-12 sm:h-12 ${panel.iconAccent}`} />
              </ContainerBox>
            </FlexContainer>

            <TypographyText
              as="h1"
              variant={{ size: '2xl' }}
              weight="bold"
              className="block text-slate-900 dark:text-white tracking-tight leading-tight mb-5 sm:mb-6 sm:text-3xl"
            >
              {panel.title}
            </TypographyText>

            <TypographyText
              variant={{ size: 'base' }}
              className="block text-slate-600 dark:text-slate-300 leading-relaxed max-w-md mx-auto"
            >
              {panel.body}
            </TypographyText>
          </motion.div>
        </ContainerBox>

        <ContainerBox className="flex flex-col items-center gap-7 sm:gap-9 mt-10 sm:mt-12 shrink-0">
          <FlexContainer align="center" justify="center" gap="xs">
            {PANELS.map((p, i) => (
              <ContainerBox
                key={p.id}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? 'w-8 bg-slate-700 dark:bg-white/80' : 'w-1.5 bg-slate-200 dark:bg-white/25'
                }`}
              />
            ))}
          </FlexContainer>

          <ActionButton
            variant={{ style: 'primary', size: 'lg' }}
            onClick={handleNext}
            className="w-full max-w-sm"
            icon={isLast ? <ArrowRight className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            iconPosition="right"
          >
            {panel.ctaText}
          </ActionButton>
        </ContainerBox>
      </ContainerBox>
    </ContainerBox>
  );
};

export default WelcomeExperienceScreen;
